import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

// Removemos date-fns por ahora y usaremos Date nativo

const DashboardTomaMuestraInternal = () => {
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;

  // Estados principales
  const [datosEstados, setDatosEstados] = useState([]);
  const [datosModulos, setDatosModulos] = useState([]);
  const [datosUsuarios, setDatosUsuarios] = useState([]);
  const [datosTiempos, setDatosTiempos] = useState([]);
  const [estadisticasGenerales, setEstadisticasGenerales] = useState({});
  const [topUsuarios, setTopUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fecha seleccionada (ahora editable)
  const [fechaSeleccionada, setFechaSeleccionada] = useState(() => new Date());

  // Filtro por sucursal
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');

  useEffect(() => {
    fetchSucursales();
    fetchDatosDashboard();
  }, []);

  const fetchSucursales = async () => {
    try {
      const response = await fetch(`${server}/DashboardKanban/dashboard/sucursales`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSucursales(data.$values || data);
      }
    } catch (error) {
      console.error('Error al obtener sucursales:', error);
    }
  };

  const fetchDatosDashboard = async (fecha = fechaSeleccionada, sucursal = sucursalSeleccionada) => {
    setLoading(true);
    try {
      const fechaInicio = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), 0, 0, 0).toISOString().slice(0, 19).replace('T', ' ');
      const fechaFin = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), 23, 59, 59).toISOString().slice(0, 19).replace('T', ' ');

      await Promise.all([
        fetchEstadisticasGenerales(fechaInicio, fechaFin, sucursal),
        fetchDatosEstados(fechaInicio, fechaFin, sucursal),
        fetchDatosModulos(fechaInicio, fechaFin, sucursal),
        fetchDatosUsuarios(fechaInicio, fechaFin, sucursal),
        fetchDatosTiempos(fechaInicio, fechaFin, sucursal)
      ]);
    } catch (error) {
      console.error('Error al obtener datos del dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar cambio de fecha
  const handleFechaChange = (event) => {
    const nuevaFecha = new Date(event.target.value + 'T00:00:00');
    setFechaSeleccionada(nuevaFecha);
    fetchDatosDashboard(nuevaFecha, sucursalSeleccionada);
  };

  // Función para manejar cambio de sucursal
  const handleSucursalChange = (event) => {
    const nuevaSucursal = event.target.value;
    setSucursalSeleccionada(nuevaSucursal);
    fetchDatosDashboard(fechaSeleccionada, nuevaSucursal);
  };

  // Función para formatear fecha para input type="date"
  const formatearFechaParaInput = (fecha) => {
    return fecha.toISOString().split('T')[0];
  };

  const fetchEstadisticasGenerales = async (fechaInicio, fechaFin, sucursal = sucursalSeleccionada) => {
    try {
      const sucursalParam = sucursal ? `&sucursalId=${sucursal}` : '';
      const response = await fetch(`${server}/DashboardKanban/dashboard/resumen?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}${sucursalParam}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        setEstadisticasGenerales({
          totalMuestras: data.totalMuestras || 0,
          completadas: data.completadas || 0,
          porcentajeCompletado: data.porcentajeCompletado || 0,
          tiempoPromedio: data.tiempoPromedioMinutos || 0
        });
      }
    } catch (error) {
      console.error('Error al obtener estadísticas generales:', error);
    }
  };

  const fetchDatosEstados = async (fechaInicio, fechaFin, sucursal = sucursalSeleccionada) => {
    try {
      const sucursalParam = sucursal ? `&sucursalId=${sucursal}` : '';
      const response = await fetch(`${server}/DashboardKanban/dashboard/estados?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}${sucursalParam}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const estadosData = data.$values || data;
        
        // Procesar datos para el gráfico de pie
        const datosFormateados = estadosData.map(item => ({
          name: item.nombreEstado,
          value: item.cantidad,
          color: getEstadoColor(item.idEstado)
        }));
        
        setDatosEstados(datosFormateados);
      }
    } catch (error) {
      console.error('Error al obtener datos de estados:', error);
    }
  };

  const fetchDatosModulos = async (fechaInicio, fechaFin, sucursal = sucursalSeleccionada) => {
    try {
      const sucursalParam = sucursal ? `&sucursalId=${sucursal}` : '';
      const response = await fetch(`${server}/DashboardKanban/dashboard/modulos?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}${sucursalParam}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const modulosData = data.$values || data;
        
        const datosFormateados = modulosData.map(item => ({
          modulo: item.modulo,
          cantidad: item.cantidad,
          completadas: item.completadas || 0,
          pendientes: item.cantidad - (item.completadas || 0)
        }));
        
        setDatosModulos(datosFormateados);
      }
    } catch (error) {
      console.error('Error al obtener datos de módulos:', error);
    }
  };

  const fetchDatosUsuarios = async (fechaInicio, fechaFin, sucursal = sucursalSeleccionada) => {
    try {
      const sucursalParam = sucursal ? `&sucursalId=${sucursal}` : '';
      const response = await fetch(`${server}/DashboardKanban/dashboard/usuarios?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}${sucursalParam}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const usuariosData = data.$values || data;
        
        // Top 10 usuarios
        const topUsuariosData = usuariosData
          .sort((a, b) => b.cantidad - a.cantidad)
          .slice(0, 10);
        
        setTopUsuarios(topUsuariosData);
        setDatosUsuarios(usuariosData);
      }
    } catch (error) {
      console.error('Error al obtener datos de usuarios:', error);
    }
  };

  const fetchDatosTiempos = async (fechaInicio, fechaFin, sucursal = sucursalSeleccionada) => {
    try {
      const sucursalParam = sucursal ? `&sucursalId=${sucursal}` : '';
      const response = await fetch(`${server}/DashboardKanban/dashboard/tiempos?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}${sucursalParam}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const tiemposData = data.$values || data;
        
        const datosFormateados = tiemposData.map(item => ({
          hora: item.hora + ':00', // Ya viene en formato HH desde el backend
          muestras: item.cantidad,
          tiempoPromedio: item.tiempoPromedio || 0
        }));
        
        setDatosTiempos(datosFormateados);
      }
    } catch (error) {
      console.error('Error al obtener datos de tiempos:', error);
    }
  };

  const getEstadoColor = (estadoId) => {
    const colores = {
      1: '#6c757d', // Pendiente - gris
      2: '#007bff', // Inicio Toma Muestra - azul
      3: '#28a745', // Toma Realizada - verde
      4: '#fd7e14', // Trasladado - naranja
      5: '#20c997', // Verificada - teal
      6: '#dc3545', // Procesada - rojo
      7: '#6610f2', // Anulada - morado
      8: '#ffc107'  // Paciente Ausente - amarillo
    };
    return colores[estadoId] || '#6c757d';
  };

  const formatearTiempo = (minutos) => {
    if (minutos < 60) {
      return `${parseFloat(minutos.toFixed(2))}m`;
    }
    const horas = Math.floor(minutos / 60);
    const minutosRestantes = parseFloat((minutos % 60).toFixed(2));
    return `${horas}h ${minutosRestantes}m`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          padding: '8px', 
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {label}
          </div>
          {payload.map((entry, index) => (
            <div key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="right_col" role="main">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div className="spinner">
            <i className="fa fa-spinner fa-spin fa-3x"></i>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="right_col" role="main">
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>Dashboard - Toma de Muestras</h3>
          </div>
          <div className="title_right">
            <div className="col-md-7 col-sm-7 form-group pull-right top_search">
              <div className="input-group">
                <span className="input-group-addon">
                  <i className="fa fa-calendar"></i>
                </span>
                <input
                  type="date"
                  className="form-control"
                  value={formatearFechaParaInput(fechaSeleccionada)}
                  onChange={handleFechaChange}
                  style={{ borderRadius: 0 }}
                />
                <span className="input-group-btn">
                  <button 
                    className="btn btn-default" 
                    type="button"
                    onClick={() => fetchDatosDashboard()}
                    title="Actualizar datos"
                  >
                    <i className="fa fa-refresh"></i>
                  </button>
                  <button 
                    className="btn btn-primary" 
                    type="button"
                    onClick={() => {
                      const hoy = new Date();
                      setFechaSeleccionada(hoy);
                      fetchDatosDashboard(hoy);
                    }}
                    title="Ir a hoy"
                  >
                    <i className="fa fa-calendar-check-o"></i> Hoy
                  </button>
                </span>
              </div>
              <div className="input-group" style={{ marginTop: '6px' }}>
                <span className="input-group-addon">
                  <i className="fa fa-building"></i>
                </span>
                <select
                  className="form-control"
                  value={sucursalSeleccionada}
                  onChange={handleSucursalChange}
                  style={{ borderRadius: 0 }}
                >
                  <option value="">Todas las sucursales</option>
                  {sucursales.map(s => (
                    <option key={s.id} value={s.id}>{s.abreviatura} - {s.nombre}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="clearfix"></div>

      {/* Estadísticas generales */}
      <div className="row">
        <div className="col-md-3 col-sm-6 col-xs-12">
          <div className="x_panel tile fixed_height_320">
            <div className="x_title">
              <h2>Total Muestras</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <h1 className="text-center" style={{fontSize: '48px', color: '#1f77b4'}}>
                {estadisticasGenerales.totalMuestras || 0}
              </h1>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 col-xs-12">
          <div className="x_panel tile fixed_height_320">
            <div className="x_title">
              <h2>Completadas</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <h1 className="text-center" style={{fontSize: '48px', color: '#2ca02c'}}>
                {estadisticasGenerales.completadas || 0}
              </h1>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 col-xs-12">
          <div className="x_panel tile fixed_height_320">
            <div className="x_title">
              <h2>% Completado</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <h1 className="text-center" style={{fontSize: '48px', color: '#ff7f0e'}}>
                {estadisticasGenerales.porcentajeCompletado || 0}%
              </h1>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 col-xs-12">
          <div className="x_panel tile fixed_height_320">
            <div className="x_title">
              <h2>Tiempo Promedio</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <h1 className="text-center" style={{fontSize: '32px', color: '#17a2b8'}}>
                {formatearTiempo(estadisticasGenerales.tiempoPromedio || 0)}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Gráfico de Estados */}
        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="x_panel">
            <div className="x_title">
              <h2>Distribución por Estados</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={datosEstados}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {datosEstados.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Gráfico de Módulos */}
        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="x_panel">
            <div className="x_title">
              <h2>Muestras por Módulo</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={datosModulos}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="modulo" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completadas" fill="#28a745" name="Completadas" />
                  <Bar dataKey="pendientes" fill="#6c757d" name="Pendientes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Actividad por Hora */}
        <div className="col-md-8 col-sm-8 col-xs-12">
          <div className="x_panel">
            <div className="x_title">
              <h2>Actividad por Hora</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={datosTiempos}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hora" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="muestras" 
                    stroke="#007bff" 
                    fill="#007bff" 
                    fillOpacity={0.3}
                    name="Muestras Procesadas"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top Usuarios */}
        <div className="col-md-4 col-sm-4 col-xs-12">
          <div className="x_panel">
            <div className="x_title">
              <h2>Top Usuarios Hoy</h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <ul className="list-unstyled top_profiles scroll-view">
                {topUsuarios.slice(0, 5).map((usuario, index) => (
                  <li key={usuario.usuario} className="media event">
                    <div className="media-object pull-left">
                      <span className="badge" style={{
                        backgroundColor: index < 3 ? '#1ABB9C' : '#6c757d',
                        color: 'white',
                        fontSize: '12px',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {index + 1}
                      </span>
                    </div>
                    <div className="media-body">
                      <div className="media-heading">{usuario.usuario}</div>
                      <p>
                        <strong>{usuario.cantidad} órdenes</strong>
                        {usuario.tiempoPromedioMinutos > 0 && (
                          <span style={{ display: 'block', fontSize: '11px', color: '#73879C' }}>
                            <i className="fa fa-clock-o"></i> Tiempo promedio: {formatearTiempo(usuario.tiempoPromedioMinutos)}
                          </span>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

const DashboardTomaMuestra = () => {
  return <DashboardTomaMuestraInternal />;
};

export default DashboardTomaMuestra;